import React, { useState, useEffect, useRef } from "react";
import { slider1, slider2, slider3} from "../../assets/data/Imagedata";

const ImageSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerHeight, setContainerHeight] = useState("auto");
  const imageContainerRef = useRef(null);

  const images = [
    {
      url: slider1,
      link: "/winning-moments",
    },
   
    {
      url: slider2,
      link: "/aboutus",
    },
    {
      url: slider3,
      link: "/aboutus",
    },
  ];

  // Function to calculate container height based on image aspect ratio
  const updateContainerHeight = () => {
    if (imageContainerRef.current) {
      const container = imageContainerRef.current;
      const containerWidth = container.offsetWidth;
      
      // Get current image
      const currentImage = images[currentIndex];
      if (currentImage) {
        const img = new Image();
        img.src = currentImage.url;
        
        img.onload = () => {
          const aspectRatio = img.height / img.width;
          const calculatedHeight = containerWidth * aspectRatio;
          
          // Set height with mobile-first approach
          if (containerWidth < 640) { // sm breakpoint
            setContainerHeight(`${Math.min(calculatedHeight, window.innerHeight * 0.6)}px`);
          } else if (containerWidth < 768) { // md breakpoint
            setContainerHeight(`${Math.min(calculatedHeight, window.innerHeight * 0.7)}px`);
          } else if (containerWidth < 1024) { // lg breakpoint
            setContainerHeight(`${Math.min(calculatedHeight, window.innerHeight * 0.75)}px`);
          } else { // xl and above
            setContainerHeight(`${Math.min(calculatedHeight, window.innerHeight * 0.8)}px`);
          }
        };
      }
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    const currentImage = images[currentIndex];
    if (currentImage.link) {
      window.location.href = currentImage.link;
    }
  };

  // Update container height on window resize and currentIndex change
  useEffect(() => {
    updateContainerHeight();
    
    const handleResize = () => {
      updateContainerHeight();
    };

    window.addEventListener("resize", handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [currentIndex]);

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [currentIndex]);

  return (
    <div className="relative w-full overflow-hidden">
      {/* Main Slider Container - Auto-adjusting height */}
      <div 
        ref={imageContainerRef}
        className="relative w-full"
        style={{ height: containerHeight || 'auto' }}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute top-0 left-0 w-full h-full transition-all duration-700 ease-in-out cursor-pointer ${
              index === currentIndex
                ? "opacity-100 z-10 translate-x-0"
                : "opacity-0 z-0 translate-x-full"
            }`}
            onClick={handleImageClick}
          >
            {/* Clickable Image Container */}
            <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-white">
              <img
                src={image.url}
                alt={`Slide ${index + 1}`}
                className="w-full h-full object-contain object-center"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                onLoad={index === currentIndex ? updateContainerHeight : undefined}
              />
            </div>

            {/* Subtle overlay for better visibility */}
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none"
              aria-hidden="true"
            />

            {/* Hover effect */}
            <div className="absolute inset-0 bg-black/0 hover:bg-black/5 active:bg-black/10 transition-all duration-300" />
          </div>
        ))}
      </div>

      {/* Dot Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 touch-manipulation ${
              index === currentIndex
                ? "bg-white scale-110 shadow-sm"
                : "bg-white/60 hover:bg-white/80"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageSlider;